import { useMemo } from "react";
import type { CountrySessionEntry } from "../../../../domain/models/metrics";
import { useLocalization } from "../../../../hooks/useLocalization";
import { CountryMetricRow } from "../types";
import { buildCountryMetricRows } from "../utils/countryMetricRows";

interface UseCountrySessionsDataResult {
  rows: CountryMetricRow[];
  isEmpty: boolean;
}

/**
 * Display rows for the "User Sessions per country" card.
 *
 * `totalSessions` is the same-filter session total from the activity chart API;
 * when it exceeds the sum of the individually-listed countries, the difference is
 * the aggregate the backend privacy-masked to 0 and belongs to "Other".
 *
 * See `buildCountryMetricRows` for the bucketing, sorting and percentage rules.
 */
export const useCountrySessionsData = (
  rawData: CountrySessionEntry[],
  totalSessions?: number,
  isUserLevelDataEnabled = false,
): UseCountrySessionsDataResult => {
  const { t } = useLocalization();

  const rows = useMemo(
    () =>
      buildCountryMetricRows({
        entries: rawData.map((entry) => ({
          country: entry.country,
          value: entry.sessionCount,
        })),
        total: totalSessions,
        revealSmallValues: isUserLevelDataEnabled,
        otherLabel: t("pages.dataAnalytics.internalUsage.countryUsage.other"),
        unknownLabel: t(
          "pages.dataAnalytics.internalUsage.countryUsage.unknown",
        ),
      }),
    [rawData, totalSessions, t, isUserLevelDataEnabled],
  );

  return {
    rows,
    isEmpty: rows.length === 0,
  };
};
